import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CommentOutlined,
  EllipsisOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Empty, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React from 'react';
import { useTranslation } from 'react-i18next';

import colors from '../theme/colors';
import {
  MobileOpenClassSessionParticipant,
  OpenClassAttendanceStatus,
  OpenClassCertificateResultStatus,
  OpenClassSessionParticipantState,
} from '../types/OpenClassSession';
import { sortParticipantsByCompany } from '../utils/openClassSessionParticipants';
import DidNotParticipate from '../resources/img/didNotParticipate.svg';
import Failed from '../resources/img/failed.svg';
import Participated from '../resources/img/participated.svg';
import Passed from '../resources/img/passed.svg';

const attendanceIcons: Record<OpenClassAttendanceStatus, string | null> = {
  not_recorded: null,
  present: Participated,
  not_present: DidNotParticipate,
};

const resultIcons: Record<OpenClassCertificateResultStatus, string | null> = {
  not_recorded: null,
  passed: Passed,
  failed: Failed,
};

export const participantStates = {
  present: (participant: MobileOpenClassSessionParticipant) => ({
    attendanceStatus: 'present' as const,
    comment: participant.attendanceComment ?? null,
  }),
  notPresent: (participant: MobileOpenClassSessionParticipant) => ({
    attendanceStatus: 'not_present' as const,
    comment: participant.attendanceComment ?? null,
  }),
  passed: (participant: MobileOpenClassSessionParticipant) => ({
    attendanceStatus: 'present' as const,
    certificateResultStatus: 'passed' as const,
    comment: participant.attendanceComment ?? null,
  }),
  failed: (participant: MobileOpenClassSessionParticipant) => ({
    attendanceStatus: 'present' as const,
    certificateResultStatus: 'failed' as const,
    comment: participant.attendanceComment ?? null,
  }),
  cleared: () => ({
    attendanceStatus: 'not_recorded' as const,
    certificateResultStatus: 'not_recorded' as const,
    comment: null,
  }),
};

type OpenClassSessionParticipantTableProps = {
  loadingRegistrationIds?: number[];
  participants: MobileOpenClassSessionParticipant[];
  selectedRegistrationIds?: number[];
  onSelectionChange?: (
    participants: MobileOpenClassSessionParticipant[],
  ) => void;
  onStateUpdate: (
    participant: MobileOpenClassSessionParticipant,
    state: OpenClassSessionParticipantState,
  ) => void;
  onCommentEdit: (participant: MobileOpenClassSessionParticipant) => void;
};

const OpenClassSessionParticipantTable: React.FC<
  OpenClassSessionParticipantTableProps
> = ({
  loadingRegistrationIds,
  participants,
  selectedRegistrationIds,
  onSelectionChange,
  onStateUpdate,
  onCommentEdit,
}) => {
  const { t } = useTranslation();

  const renderStatus = (participant: MobileOpenClassSessionParticipant) => {
    const marks: { status: string; src: string }[] = [];
    const resultIcon = resultIcons[participant.certificateResultStatus];
    const attendanceIcon = attendanceIcons[participant.attendanceStatus];

    if (resultIcon) {
      marks.push({
        status: participant.certificateResultStatus,
        src: resultIcon,
      });
    }
    if (attendanceIcon) {
      marks.push({ status: participant.attendanceStatus, src: attendanceIcon });
    }

    if (marks.length === 0) {
      return (
        <span
          aria-label={t(
            'components.OpenClassSessionParticipantTable.not_recorded',
          )}
          className="text-blueGrayMediumDark"
          role="img">
          -
        </span>
      );
    }

    return (
      <div className="flex gap-2 justify-end">
        {marks.map(({ status, src }) => (
          <img
            alt={t(`components.OpenClassSessionParticipantTable.${status}`)}
            className="w-4"
            key={status}
            src={src}
          />
        ))}
      </div>
    );
  };

  const renderCertificate = ({
    certificateType,
    certificateResultStatus,
  }: MobileOpenClassSessionParticipant) => {
    const passed = certificateResultStatus === 'passed';

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs whitespace-nowrap ${
          passed
            ? 'bg-successLight text-success'
            : 'bg-blueGrayLight text-safeLifeDarker'
        }`}>
        {passed && <CheckCircleOutlined />}
        {t(`components.OpenClassSessionParticipantTable.${certificateType}`)}
      </span>
    );
  };

  const rowMenuItems = (participant: MobileOpenClassSessionParticipant) => {
    const applies = (state: OpenClassSessionParticipantState) => () =>
      onStateUpdate(participant, state);

    return [
      {
        key: 'present',
        label: (
          <div
            className="flex gap-2 items-center"
            onClick={applies(participantStates.present(participant))}>
            <img src={Participated} className="w-4" alt="" />
            {t('components.OpenClassSessionParticipantTable.present')}
          </div>
        ),
      },
      {
        key: 'not-present',
        label: (
          <div
            className="flex gap-2 items-center"
            onClick={applies(participantStates.notPresent(participant))}>
            <CloseCircleOutlined style={{ color: colors.warning }} />
            {t('components.OpenClassSessionParticipantTable.not_present')}
          </div>
        ),
      },
      {
        key: 'passed',
        label: (
          <div
            className="flex gap-2 items-center"
            onClick={applies(participantStates.passed(participant))}>
            <img src={Passed} className="w-4" alt="" />
            {t('components.OpenClassSessionParticipantTable.passed')}
          </div>
        ),
      },
      {
        key: 'failed',
        label: (
          <div
            className="flex gap-2 items-center"
            onClick={applies(participantStates.failed(participant))}>
            <img src={Failed} className="w-4" alt="" />
            {t('components.OpenClassSessionParticipantTable.failed')}
          </div>
        ),
      },
      {
        key: 'comment',
        label: (
          <div
            className="flex gap-2 items-center"
            onClick={() => onCommentEdit(participant)}>
            <CommentOutlined style={{ color: colors.safeLifeDarker }} />
            {t('components.OpenClassSessionParticipantTable.comment')}
          </div>
        ),
      },
      {
        key: 'clear',
        label: (
          <div
            className="flex gap-2 items-center"
            onClick={applies(participantStates.cleared())}>
            <MinusCircleOutlined style={{ color: colors.safeLifeDarker }} />
            {t('components.OpenClassSessionParticipantTable.clear')}
          </div>
        ),
      },
    ];
  };

  const header = (key: string) => (
    <span className="text-xs whitespace-nowrap">
      {t(`components.OpenClassSessionParticipantTable.${key}`)}
    </span>
  );

  const columns: ColumnsType<MobileOpenClassSessionParticipant> = [
    {
      title: header('name'),
      dataIndex: 'name',
      render: (
        name: string,
        participant: MobileOpenClassSessionParticipant,
      ) => (
        <div className="flex flex-col min-w-0">
          <span className="font-medium truncate">{name}</span>
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-xs text-blueGrayDark truncate">
              {participant.clientCompany?.name ??
                t('components.OpenClassSessionParticipantTable.noCompany')}
            </span>
            {/* The note would wrap over several lines, so the row only flags that one exists. It
                sits with the company rather than the name to keep the name line clean. */}
            {participant.attendanceComment && (
              <button
                aria-label={t(
                  'components.OpenClassSessionParticipantTable.comment',
                )}
                className="shrink-0 leading-none text-xs text-blueGrayMediumDark"
                onClick={() => onCommentEdit(participant)}
                type="button">
                <CommentOutlined />
              </button>
            )}
          </div>
        </div>
      ),
    },
    {
      title: header('status'),
      align: 'right',
      width: 60,
      render: (_: unknown, participant: MobileOpenClassSessionParticipant) =>
        renderStatus(participant),
    },
    {
      title: header('certificate'),
      align: 'right',
      width: 88,
      render: (_: unknown, participant: MobileOpenClassSessionParticipant) =>
        renderCertificate(participant),
    },
    {
      title: '',
      dataIndex: 'registrationId',
      align: 'right',
      width: 36,
      render: (
        registrationId: number,
        participant: MobileOpenClassSessionParticipant,
      ) => (
        <Dropdown
          menu={{ items: rowMenuItems(participant) }}
          placement="bottomRight"
          trigger={['click']}
          className="inline-block">
          <Button
            aria-label={t(
              'components.OpenClassSessionParticipantTable.actions',
            )}
            disabled={loadingRegistrationIds?.includes(registrationId)}
            icon={<EllipsisOutlined />}
            type="text"
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={sortParticipantsByCompany(participants)}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('common.noData')}
          />
        ),
      }}
      pagination={false}
      rowClassName={(participant) =>
        participant.attendanceStatus === 'not_recorded' ? 'bg-warningBG' : ''
      }
      rowKey={(participant) => participant.registrationId}
      size="small"
      tableLayout="fixed"
      rowSelection={
        onSelectionChange && {
          selectedRowKeys: selectedRegistrationIds,
          onChange: (_, selected) => onSelectionChange(selected),
        }
      }
    />
  );
};

export default OpenClassSessionParticipantTable;
